import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Globe, Search, Filter } from 'lucide-react';

interface ZonesEmptyStateProps {
  isFiltered: boolean;
  onAddZone: () => void;
  onClearFilters: () => void;
  searchTerm: string;
  selectedProvider: string;
}

const ZonesEmptyState: React.FC<ZonesEmptyStateProps> = ({
  isFiltered,
  onAddZone,
  onClearFilters,
  searchTerm,
  selectedProvider,
}) => {
  if (isFiltered) {
    return (
      <motion.div
        className="flex flex-col items-center justify-center py-16 px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6">
          <Search className="w-10 h-10 text-slate-400" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
          No zones found
        </h3>
        <p className="text-slate-600 dark:text-slate-400 text-center max-w-md mb-6">
          {searchTerm && selectedProvider !== 'all' ? (
            <>No zones match your search "{searchTerm}" in the selected provider.</>
          ) : searchTerm ? (
            <>No zones match your search "{searchTerm}". Try adjusting your search terms.</>
          ) : selectedProvider !== 'all' ? (
            <>
              No zones found for the selected provider. Try selecting a different provider or
              clearing the filter.
            </>
          ) : (
            <>No zones match your current filters. Try adjusting your search criteria.</>
          )}
        </p>
        <div className="flex items-center space-x-3">
          <motion.button
            className="inline-flex items-center justify-center px-4 py-2 text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            onClick={onClearFilters}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Filter className="w-4 h-4" />
            <span>Clear Filters</span>
          </motion.button>
          <motion.button
            className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            onClick={onAddZone}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-4 h-4" />
            <span>Add New Zone</span>
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="flex flex-col items-center justify-center py-16 px-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center mb-6">
        <Globe className="w-12 h-12 text-blue-600 dark:text-blue-400" />
      </div>
      <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">No DNS Zones Yet</h3>
      <p className="text-slate-600 dark:text-slate-400 text-center max-w-lg mb-8 leading-relaxed">
        Get started by adding your first DNS zone. You can manage zones from multiple providers like
        Cloudflare, AWS Route53, Azure DNS, and more all in one place.
      </p>

      <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-8">
        <motion.button
          className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-lg px-6 py-3"
          onClick={onAddZone}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Plus className="w-5 h-5" />
          <span>Add Your First Zone</span>
        </motion.button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-3">
            <span className="text-green-600 dark:text-green-400 text-xl">🔶</span>
          </div>
          <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Multi-Provider</h4>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Support for Cloudflare, AWS, Azure, GCP, and more
          </p>
        </div>

        <div className="text-center">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Globe className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Easy Management</h4>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Intuitive interface for DNS record management
          </p>
        </div>

        <div className="text-center">
          <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mx-auto mb-3">
            <span className="text-purple-600 dark:text-purple-400 text-xl">⚡</span>
          </div>
          <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Real-time Sync</h4>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Changes sync instantly with your DNS provider
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default ZonesEmptyState;
